import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

/**
 * Fish Wishlist + Cult Design Cart Drawer Integration
 *
 * Bridges the gap between Fish Wishlist's legacy cart AJAX API calls and
 * the theme's standard events architecture (`@shopify/events`, `<cart-items-component>`, `<cart-icon>`).
 */

/**
 * Returns all cart section IDs on the current page that should be re-rendered.
 * @returns {string[]}
 */
function getCartSectionIds() {
  const cartItemsComponents = document.querySelectorAll('cart-items-component');
  const sectionIds = Array.from(cartItemsComponents)
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);

  if (sectionIds.length === 0) {
    sectionIds.push('cart-drawer-section');
  }

  return [...new Set(sectionIds)];
}

/**
 * Closes the Fish Wishlist drawer.
 */
export function closeWishlistDrawer() {
  const wishlistDrawer = document.querySelector('wishlists-drawer');
  if (wishlistDrawer && (wishlistDrawer.dataset.open === 'true' || wishlistDrawer.classList.contains('active'))) {
    if (typeof wishlistDrawer.close === 'function') {
      wishlistDrawer.close();
    } else {
      wishlistDrawer.dataset.open = 'false';
      wishlistDrawer.classList.remove('active');
      document.body.classList.remove('overflow-hidden');
      document.documentElement.classList.remove('lock');
    }
  }
}

let drawerTransitionTimer = null;

/**
 * Closes the Wishlist drawer and opens the Mini Cart drawer (debounced and checks if already open).
 */
export function openCartDrawerAndCloseWishlist() {
  if (drawerTransitionTimer) {
    clearTimeout(drawerTransitionTimer);
  }

  drawerTransitionTimer = setTimeout(() => {
    drawerTransitionTimer = null;

    // 1. Close Wishlist drawer
    closeWishlistDrawer();

    // 2. Open Cart drawer ONLY if not already open (prevents duplicate animation / closing-reopening)
    const cartDrawer = document.querySelector('theme-drawer#cart-drawer');
    if (cartDrawer) {
      if (!cartDrawer.isOpen && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }
    } else if (window.Shopify?.actions?.openCart) {
      window.Shopify.actions.openCart();
    }
  }, 200);
}

let lastHandledTimestamp = 0;

/**
 * Dispatches standard CartLinesUpdateEvent to update cart drawer HTML and cart bubble count.
 * @param {Array<any>} [items]
 * @param {Record<string, string>} [sections]
 */
export async function syncCartFromWishlist(items = [], sections = null) {
  const sectionIds = getCartSectionIds();
  const cartUrl = window.Theme?.routes?.cart_url || '/cart';

  let ajaxCart = null;
  let sectionsData = sections;

  try {
    if (!sectionsData) {
      const [sectionsRes, cartRes] = await Promise.all([
        fetch(`${cartUrl}?sections=${encodeURIComponent(sectionIds.join(','))}`, {
          headers: { Accept: 'application/json' },
        }).then((r) => (r.ok ? r.json() : null)),
        fetch(`${cartUrl}.js`, {
          headers: { Accept: 'application/json' },
        }).then((r) => (r.ok ? r.json() : null)),
      ]);
      sectionsData = sectionsRes;
      ajaxCart = cartRes;
    } else {
      ajaxCart = await fetch(`${cartUrl}.js`, {
        headers: { Accept: 'application/json' },
      }).then((r) => (r.ok ? r.json() : null));
    }
  } catch (error) {
    console.warn('[fish-wishlist-cart] Error synchronizing cart data:', error);
  }

  if (!ajaxCart) return;

  const deferredEventPromise = CartLinesUpdateEvent.createPromise();
  const rawItems = items.length > 0 ? items : ajaxCart.items || [];
  const lines = rawItems.map((item) => ({
    merchandiseId: String(item.variant_id || item.id),
    quantity: item.quantity || 1,
  }));

  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines,
      promise: deferredEventPromise.promise,
    })
  );

  deferredEventPromise.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
    detail: {
      items: ajaxCart.items,
      itemCount: ajaxCart.item_count,
      sections: sectionsData,
      source: 'fish-wishlist',
      didError: false,
    },
  });
}

// 1. Intercept window.fetch to automatically include sections when adding to cart
const nativeFetch = window.fetch;

window.fetch = async function (input, init) {
  const url = typeof input === 'string' ? input : input?.url;
  const isCartAdd = typeof url === 'string' && (url.includes('/cart/add') || url.includes('/cart/add.js'));

  if (isCartAdd && init?.method && init.method.toUpperCase() === 'POST') {
    let isFishWishlist = false;

    try {
      if (typeof init.body === 'string' && init.body.trim().startsWith('{')) {
        if (init.body.includes('_fish_wishlist')) {
          isFishWishlist = true;
        }

        const parsed = JSON.parse(init.body);
        if (!parsed.sections) {
          const sectionIds = getCartSectionIds();
          parsed.sections = sectionIds.join(',');
          parsed.sections_url = window.location.pathname;
          init = {
            ...init,
            body: JSON.stringify(parsed),
          };
        }
      }
    } catch {
      // Proceed with original init on JSON parse failure
    }

    const response = await nativeFetch.call(this, input, init);

    if (response.ok && isFishWishlist) {
      try {
        const clone = response.clone();
        clone
          .json()
          .then(async (data) => {
            lastHandledTimestamp = Date.now();
            const addedItems = data.items || (data.id ? [data] : []);
            await syncCartFromWishlist(addedItems, data.sections);
            openCartDrawerAndCloseWishlist();
          })
          .catch(() => {});
      } catch {
        // ignore clone/parse errors
      }
    }

    return response;
  }

  return nativeFetch.call(this, input, init);
};

// 2. Official event listener fallback for Fish Wishlist
document.addEventListener('fishwishlist:item-added-to-cart', async (event) => {
  const isRecentlyHandled = Date.now() - lastHandledTimestamp < 800;

  if (!isRecentlyHandled) {
    lastHandledTimestamp = Date.now();
    const detail = event.detail || {};
    const items = detail.items || (detail.variantId ? [{ id: detail.variantId, quantity: detail.quantity || 1 }] : []);
    await syncCartFromWishlist(items);
    openCartDrawerAndCloseWishlist();
  }
});
